import { useMemo, useState } from "react";

import { useGetMargin, useRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import type { Product } from "~/v2/api/rest/account";
import { getPortfolioHistory } from "~/v2/api/rest/portfolio";
import {
  alignPortfolioHistoryToEquity,
  clipPortfolioHistoryToMonths,
  getPortfolioChange,
  type PortfolioHistoryChartPoint,
  toPortfolioHistoryChartData,
} from "~/v3-poc/features/portfolio-history/portfolioHistory";
import {
  DEFAULT_PORTFOLIO_LOOKBACK,
  getPortfolioLookbackOption,
  type PortfolioLookback,
} from "~/v3-poc/features/portfolio-history/portfolioHistoryTimeSettings";
import { parseMoney } from "../overview.format";

export interface UseAccountCardControllerParams {
  accountId: string | undefined;
  product: Product | undefined;
}

export interface AccountCardControllerValue {
  netLiq: number | undefined;
  change: number | undefined;
  changePercent: number | undefined;
  data: PortfolioHistoryChartPoint[];
  baseline: number | undefined;
  lookback: PortfolioLookback;
  onLookbackChange: (lookback: PortfolioLookback) => void;
  isLoading: boolean;
  hasError: boolean;
}

export const useAccountCardController = ({
  accountId,
  product,
}: UseAccountCardControllerParams): AccountCardControllerValue => {
  const isAccountReady = Boolean(accountId && product);
  const [lookback, setLookback] = useState<PortfolioLookback>(
    DEFAULT_PORTFOLIO_LOOKBACK,
  );
  const lookbackOption = getPortfolioLookbackOption(lookback);

  const { margin, isLoading: isMarginLoading } = useGetMargin({
    accountID: accountId ?? "",
    paper: product === "paper",
    options: {
      enabled: isAccountReady,
      meta: { showErrorToast: false },
    },
  });
  const netLiq = parseMoney(margin?.equity);

  const {
    data: portfolio,
    isLoading,
    isError,
  } = useRequest(
    queryKeys.portfolioHistory({
      accountID: accountId ?? "",
      product: product ?? "",
      period: lookbackOption.period,
      timeframe: lookbackOption.timeframe,
      intradayReporting: lookbackOption.intradayReporting,
    }),
    () =>
      getPortfolioHistory({
        accountID: accountId ?? "",
        product: product ?? "",
        period: lookbackOption.period,
        timeframe: lookbackOption.timeframe,
        intraday_reporting: lookbackOption.intradayReporting,
      }),
    {
      enabled: isAccountReady,
      gcTime: 5 * 60 * 1_000,
      staleTime: 2 * 60 * 1_000,
      meta: { showErrorToast: false },
    },
  );

  const data = useMemo(() => {
    const history = clipPortfolioHistoryToMonths(
      toPortfolioHistoryChartData(portfolio ?? {}),
      lookbackOption.historyMonths,
    );
    return alignPortfolioHistoryToEquity(history, netLiq);
  }, [portfolio, lookbackOption.historyMonths, netLiq]);

  const start = data[0]?.value;
  const { change, changePercent } = getPortfolioChange(data);

  return {
    netLiq,
    change,
    changePercent,
    data,
    baseline: start,
    lookback,
    onLookbackChange: setLookback,
    isLoading: isAccountReady && (isLoading || isMarginLoading),
    hasError: isError,
  };
};
